import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_LOW, EditorState, KEY_DOWN_COMMAND, KEY_ENTER_COMMAND, PASTE_COMMAND } from 'lexical';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import './LexicalEditor.scss';

import { AutoFocusPlugin } from './plugins/AutoFocusPlugin/AutoFocusPlugin';
import ClearContentPlugin from './plugins/ClearContentPlugin';
import GetMarkdownContentPlugin from './plugins/GetMarkdownContentPlugin';
import { SetContentPlugin } from './plugins/SetContentPlugin';
import PlaygroundEditorTheme from './themes/PlaygroundEditorTheme';
import './themes/PlaygroundEditorTheme.css';

const SHOULD_PRESERVE_NEW_LINES_IN_MARKDOWN = false;
const LIST_STRICT_INDENT = true;
const TABLE_CELL_MERGE = true;
const TABLE_CELL_BACKGROUND_COLOR = false;

interface LexicalEditorProps {
    onChange: (value: string) => void;
    placeholder?: string;
    autoFocus?: boolean;
    maxLength?: number;
    onKeyDown?: (event: KeyboardEvent) => void;
    onPaste?: (event: ClipboardEvent) => void;
    className?: string;
    id?: string;
    ariaLabelledby?: string;
}

export interface LexicalEditorRef {
    setContent: (markdown: string) => void;
    getContent: () => string;
    clearContent: () => void;
    focus: () => void;
    focusAtEnd: () => void;
}

function PastePlugin({ onPaste }: { onPaste?: (event: ClipboardEvent) => void }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        if (!onPaste) return;

        const removeListener = editor.registerCommand(
            PASTE_COMMAND,
            (event: ClipboardEvent) => {
                onPaste(event);
                return false; // Don't prevent default unless explicitly handled
            },
            COMMAND_PRIORITY_LOW
        );

        return removeListener;
    }, [editor, onPaste]);

    return null;
}

// Custom plugin to handle key events
function KeyDownPlugin({ onKeyDown }: { onKeyDown?: (event: KeyboardEvent) => void }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        if (!onKeyDown) return;

        // Gestionnaire spécifique pour la touche Enter
        const removeEnterListener = editor.registerCommand(
            KEY_ENTER_COMMAND,
            (event: KeyboardEvent | null) => {
                if (event && event.key === 'Enter' && !event.shiftKey) {
                    // Appeler notre gestionnaire personnalisé
                    onKeyDown(event);
                    // Si preventDefault a été appelé, empêcher le comportement par défaut de Lexical
                    return event.defaultPrevented;
                }
                return false;
            },
            COMMAND_PRIORITY_HIGH
        );

        // Gestionnaire général pour les autres touches
        const removeGeneralListener = editor.registerCommand(
            KEY_DOWN_COMMAND,
            (event: KeyboardEvent) => {
                // Ne pas traiter Enter ici, c'est géré par KEY_ENTER_COMMAND
                if (event.key === 'Enter') {
                    return false;
                }
                
                onKeyDown(event);
                return false;
            },
            COMMAND_PRIORITY_LOW
        );

        return () => {
            removeEnterListener();
            removeGeneralListener();
        };
    }, [editor, onKeyDown]);

    return null;
}


// Helper function to safely truncate markdown without breaking syntax
const truncateMarkdownSafely = (markdown: string, maxLength: number): string => {
    if (markdown.length <= maxLength) return markdown;

    // Find the last complete word within the limit
    let truncated = markdown.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');

    if (lastSpace > 0) {
        truncated = truncated.substring(0, lastSpace);
    }

    // Check for unclosed markdown syntax
    const openBold = (truncated.match(/\*\*/g) || []).length % 2 !== 0;
    const openItalic = (truncated.match(/(?<!\*)\*(?!\*)/g) || []).length % 2 !== 0;
    const openCode = (truncated.match(/`/g) || []).length % 2 !== 0;

    // Remove unclosed formatting to prevent broken markdown
    if (openBold) {
        const lastBold = truncated.lastIndexOf('**');
        if (lastBold >= 0) {
            truncated = truncated.substring(0, lastBold);
        }
    }
    if (openItalic) {
        const lastItalic = truncated.lastIndexOf('*');
        if (lastItalic >= 0) {
            truncated = truncated.substring(0, lastItalic);
        }
    }
    if (openCode) {
        const lastCode = truncated.lastIndexOf('`');
        if (lastCode >= 0) {
            truncated = truncated.substring(0, lastCode);
        }
    }

    return truncated.trim() + '...';
};

export const LexicalEditor = forwardRef<LexicalEditorRef, LexicalEditorProps>(({
    onChange,
    placeholder = 'Enter text...',
    autoFocus = false,
    maxLength,
    onKeyDown,
    onPaste,
    className = '',
    id,
    'ariaLabelledby': ariaLabelledby,
}, ref) => {
    // Refs for content management
    const setContentRef = useRef<(content: string) => void>(() => { });
    const getContentRef = useRef<() => string>(() => '');
    const clearContentRef = useRef<() => void>(() => { });

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
        setContent: (markdown: string) => {
            setContentRef.current(markdown);
        },
        getContent: () => {
            return getContentRef.current();
        },
        clearContent: () => {
            clearContentRef.current();
        },
         focus: () => {
            // Focus the editor without moving the cursor - it is safer to call this, to prevent content from disappearing
            const contentEditable = document.querySelector('.lexical-content-editable') as HTMLElement;
            if (contentEditable) {
                contentEditable.focus();
            }
        },
        focusAtEnd: () => {
            // Focus the editor and position cursor at the end
            const contentEditable = document.querySelector('.lexical-content-editable') as HTMLElement;
            if (contentEditable) {
                contentEditable.focus();
                // Use setTimeout to ensure the focus operation is complete
                setTimeout(() => {
                    const selection = window.getSelection();
                    if (selection) {
                        selection.selectAllChildren(contentEditable);
                        selection.collapseToEnd();
                    }
                }, 0);
            }
        },
    }), []);

    const initialConfig = {
        namespace: 'LexicalEditor',
        theme: PlaygroundEditorTheme,
        onError: (error: Error) => {
            console.error('Lexical error:', error);
        },
        nodes: [
            HeadingNode,
            ListNode,
            ListItemNode,
            QuoteNode,
            LinkNode,
            CodeNode,
            CodeHighlightNode,
            HorizontalRuleNode,
            TableNode,
            TableCellNode,
            TableRowNode
        ]
    };

    const handleEditorChange = useCallback((editorState: EditorState) => {
        editorState.read(() => {
            try {
                const markdown = getContentRef.current();

                // Enforce maxLength if specified using safe truncation
                if (maxLength && markdown.length > maxLength) {
                    const truncatedMarkdown = truncateMarkdownSafely(markdown, maxLength);
                    onChange(truncatedMarkdown);
                    return;
                }

                // Update a data attribute on the content editable so CSS can reliably
                // hide/show the placeholder even when the editor contains invisible
                // nodes like <br> or zero-width spaces.
                try {
                    const contentEditable = document.querySelector('.lexical-content-editable') as HTMLElement | null;
                    if (contentEditable) {
                        const hasContent = !!markdown && markdown.trim().length > 0;
                        contentEditable.setAttribute('data-has-content', hasContent ? 'true' : 'false');
                    }
                } catch (e) {
                    // noop - defensive in case DOM is not available in some test env
                }

                onChange(markdown);
            } catch (error) {
                console.error('Error converting to markdown:', error);
            }
        });
    }, [onChange, maxLength]);

    return (
        <div className={`lexical-editor-container ${className}`}>
            <LexicalComposer initialConfig={initialConfig}>
                <RichTextPlugin
                    contentEditable={
                        <ContentEditable
                            className="lexical-content-editable"
                            id={id}
                            role="textbox"
                            tabIndex={0}
                            aria-labelledby={ariaLabelledby || id ? id + "-label" : undefined}
                        />
                    }
                    placeholder={
                        <div className="lexical-placeholder">{placeholder}</div>
                    }
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <HistoryPlugin />
                <MarkdownShortcutPlugin />
                <ListPlugin />
                <CheckListPlugin />
                <TablePlugin
                    hasCellMerge={TABLE_CELL_MERGE}
                    hasCellBackgroundColor={TABLE_CELL_BACKGROUND_COLOR}
                />
                <OnChangePlugin onChange={handleEditorChange} />

                <AutoFocusPlugin autoFocus={autoFocus} />
                <KeyDownPlugin onKeyDown={onKeyDown} />
                <PastePlugin onPaste={onPaste} />

                <SetContentPlugin
                    onSetContent={setContentRef}
                    shouldPreserveNewLinesInMarkdown={SHOULD_PRESERVE_NEW_LINES_IN_MARKDOWN}
                />
                <GetMarkdownContentPlugin
                    onGetContent={getContentRef}
                    shouldPreserveNewLinesInMarkdown={SHOULD_PRESERVE_NEW_LINES_IN_MARKDOWN}
                />
                <ClearContentPlugin onClear={clearContentRef} />

            </LexicalComposer>
        </div>
    );
});


LexicalEditor.displayName = 'LexicalEditor';